import { computed, inject, Injector, signal } from "@angular/core";
import { CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField } from "@eforall/common";
import { Note, NoteCategoryId } from "@interfaces";
import { DomainDataService } from "../../../../services";

export interface NotesEditorFields {
	noteCategoryId: number;
	subject: string;
	note: string;
}


export class NoteEditorForm {

	public readonly form = new CommonForm<NotesEditorFields>(inject(Injector));
	public values = signal<NotesEditorFields | undefined>(undefined);
	private readonly domainDataService = inject(DomainDataService);

	showForm = signal<boolean>(false);

	setValues(noteData?: Note) {
		// Reset form state to ensure pending values are set to null before setting new values
		this.form.resetAll();
		if (!noteData) {
			this.values.set(undefined);
			return;
		}
		const { noteCategoryId, subject, note } = noteData;
		this.values.set({ noteCategoryId, subject, note });
	}


	private readonly contactMethodOptions = computed(() => {
		return this.domainDataService.data().noteCategories.map(v => ({
			value: v.noteCategoryId,
			text: v.name,
			icon: v.noteCategoryId === NoteCategoryId.RedFlag ? 'red fa-flag fas fa-fw text-danger' : 'far fa-sticky-note fa-fw',
		}));
	});

	readonly category = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Category ',
			options: this.contactMethodOptions(),
			isRequired: true, isSpanish: false
		}),
		actualValue: computed(() => this.values()?.noteCategoryId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'noteCategoryId',
		form: this.form,
	}));


	readonly subject = computed<CommonTextField>(() => ({
		config: signal({ label: 'Subject', min: 1, max: 60, fixCase: 'sentence-case', isRequired: true, multiLine: false, isSpanish: false }),
		actualValue: computed(() => this.values()?.subject ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'subject',
		form: this.form,
	}));

	readonly note = computed<CommonTextField>(() => ({
		config: signal({ label: 'Note', min: 3, fixCase: 'sentence-case', isRequired: true, multiLine: true, isSpanish: false }),
		actualValue: computed(() => this.values()?.note ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'note',
		form: this.form,
	}));
}